import { ReactNode, useState } from 'react';

import { isMessageV2DTO } from 'support/helpers/transactions/transactions';
import { MessageV2DTO, PartnerDTO, SenderEnvelopeDTO, TransactionOriginApp } from 'support/types';
import { Sidebar } from 'components/Sidebar/Sidebar';

import { RelatedMessages } from './components/RelatedMessages/RelatedMessages';
import { ResizablePanelHandle } from './components/ResizablePanelHandle';
import { PDFViewerPanel } from './components/WebEDIDocumentProperties/components/PDFViewerPanel/PDFViewerPanel';
import { WebEDIDocumentProperties } from './components/WebEDIDocumentProperties/WebEDIDocumentProperties';
import { useResizablePanel } from './useResizablePanel';

export { WebEDIDocumentPropertiesKeys as DocumentPropertiesKeys } from './components/WebEDIDocumentProperties/WebEDIDocumentPropertiesKeys';

type DocumentDTO = MessageV2DTO | SenderEnvelopeDTO;

type PDFViewerState = {
  url: string;
  filename: string;
};

type WebEDISidebarProps = {
  document: DocumentDTO | undefined;
  documentOriginApp: TransactionOriginApp;
  relatedTransactions: Array<DocumentDTO> | undefined;
  currentPartner: PartnerDTO | undefined;
  children?: ReactNode;
  isSenderEnvelope?: boolean;
};

export const WebEDISidebar = ({
  document,
  currentPartner,
  relatedTransactions,
  children,
  isSenderEnvelope,
  documentOriginApp,
}: WebEDISidebarProps) => {
  const [pdfViewer, setPdfViewer] = useState<PDFViewerState | null>(null);
  const { panelWidth, isCollapsed, handleMouseDown, expand, restoreNav } = useResizablePanel(!!pdfViewer);

  const isMessage = document && isMessageV2DTO(document);
  const senderPartner = isSenderEnvelope ? currentPartner : isMessage ? document.sender_partner : undefined;
  const receiverPartner = isSenderEnvelope ? null : isMessage ? document.receiver_partner : null;
  const otherPartner = currentPartner && senderPartner?.id === currentPartner?.id ? receiverPartner : senderPartner;

  if (pdfViewer) {
    return (
      <aside
        className="relative flex flex-col border-l border-gray-200"
        style={{ width: panelWidth, height: `var(--content-height)` }}
      >
        <ResizablePanelHandle onMouseDown={handleMouseDown} />
        <PDFViewerPanel
          pdfUrl={pdfViewer.url}
          filename={pdfViewer.filename}
          onClose={() => {
            setPdfViewer(null);
            restoreNav();
          }}
          isCollapsed={isCollapsed}
          onExpand={expand}
        />
      </aside>
    );
  }

  return (
    <Sidebar bottomSlot={children}>
      <WebEDIDocumentProperties
        partner={otherPartner}
        document={document}
        documentOriginApp={documentOriginApp}
        currentPartner={currentPartner}
        onViewPDF={(url, filename) => setPdfViewer({ url, filename })}
      />
      <RelatedMessages
        activeMessageId={document?.id}
        currentPartner={currentPartner}
        relatedMessages={relatedTransactions}
      />
    </Sidebar>
  );
};
